import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/data";
import { Input } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { useToast } from "@/app/Toast";
import { RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

interface AuditEntry {
  id: number; action: string; entityType: string; entityId: string | null;
  beforeJson: unknown; afterJson: unknown; createdAt: string;
  ipHash: string | null; requestId: string | null;
  actor: { id: number; email: string; firstName: string; lastName: string } | null;
}

export function AuditPage() {
  const { toast } = useToast();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityType) params.set("entityType", entityType);
      if (action) params.set("action", action);
      if (actorUserId) params.set("actorUserId", actorUserId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      params.set("limit", "100");
      const data = await api.getEnvelope<{ data: AuditEntry[]; meta: { total: number } }>(`/api/v1/audit-logs?${params.toString()}`);
      setEntries(data.data);
      setTotal(data.meta.total);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [entityType, action, actorUserId, from, to]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="entity_type (e.g. booking)" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="w-56" />
            <Input placeholder="action (e.g. booking.created)" value={action} onChange={(e) => setAction(e.target.value)} className="w-56" />
            <Input type="number" placeholder="actor_user_id" value={actorUserId} onChange={(e) => setActorUserId(e.target.value)} className="w-32" />
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-36" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-36" />
            <p className="text-xs text-muted-foreground">({total} total)</p>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Request</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <Row
                    key={e.id}
                    entry={e}
                    expanded={expanded.has(e.id)}
                    onToggle={() => {
                      const next = new Set(expanded);
                      if (next.has(e.id)) next.delete(e.id); else next.add(e.id);
                      setExpanded(next);
                    }}
                  />
                ))}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">No audit entries match these filters.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ entry, expanded, onToggle }: { entry: AuditEntry; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="w-6">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </TableCell>
        <TableCell className="text-xs">{formatDateTime(entry.createdAt)}</TableCell>
        <TableCell className="font-mono text-xs">{entry.action}</TableCell>
        <TableCell className="font-mono text-xs">
          {entry.entityType}
          {entry.entityId ? <span className="text-muted-foreground"> · {entry.entityId}</span> : null}
        </TableCell>
        <TableCell className="text-xs">
          {entry.actor ? `${entry.actor.firstName} ${entry.actor.lastName}` : <span className="text-muted-foreground">system</span>}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {entry.requestId ? `req ${entry.requestId.slice(0, 8)}` : ""}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30">
            <div className="grid gap-3 p-3 sm:grid-cols-2">
              <details open>
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">before</summary>
                <pre className="mt-1 max-h-60 overflow-auto rounded bg-background p-2 font-mono text-[11px]">
{entry.beforeJson == null ? "—" : JSON.stringify(entry.beforeJson, null, 2)}
                </pre>
              </details>
              <details open>
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">after</summary>
                <pre className="mt-1 max-h-60 overflow-auto rounded bg-background p-2 font-mono text-[11px]">
{entry.afterJson == null ? "—" : JSON.stringify(entry.afterJson, null, 2)}
                </pre>
              </details>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
